import { useState, useEffect } from "react";
import classNames from "classnames";
import { IVideos } from "../../../../utils/types";

interface Props {
  item: IVideos | null;
  onClose: () => void;
  getGuestTypeName: (eligibilityCriteriaId: string) => string;
}

const OurStoryPreview = ({ item, onClose, getGuestTypeName }: Props) => {
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    if (item) setImageIndex(0);
  }, [item]);

  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setImageIndex((i) => Math.min((item.images?.length ?? 1) - 1, i + 1));
      if (e.key === "ArrowLeft") setImageIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, onClose]);

  if (!item) return null;

  const isVideo = item.mediaType === "video" || !item.images?.length;
  const images = item.images ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col md:flex-row w-[95vw] max-w-7xl max-h-[95vh] overflow-y-auto md:overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
          onClick={onClose}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Media panel */}
        <div className="bg-black flex items-center justify-center relative overflow-hidden h-[50vw] min-h-[200px] max-h-[50vh] md:flex-1 md:h-auto md:max-h-full">
          {isVideo ? (
            item.url ? (
              <video
                src={item.url}
                controls
                autoPlay={false}
                className="w-full h-full max-h-[60vh] md:max-h-[90vh] object-contain"
              />
            ) : (
              <span className="text-white/50 text-sm">No video available</span>
            )
          ) : images.length > 0 ? (
            <>
              <img
                src={images[imageIndex]}
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40"
              />
              <img
                src={images[imageIndex]}
                alt={`${item.name} — ${imageIndex + 1}`}
                className="relative z-10 w-full h-full max-h-[60vh] md:max-h-[90vh] object-contain"
              />
              {images.length > 1 && (
                <>
                  <button
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 h-9 w-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors disabled:opacity-30 text-xl leading-none"
                    onClick={() => setImageIndex((i) => Math.max(0, i - 1))}
                    disabled={imageIndex === 0}
                  >
                    ‹
                  </button>
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 h-9 w-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors disabled:opacity-30 text-xl leading-none"
                    onClick={() => setImageIndex((i) => Math.min(images.length - 1, i + 1))}
                    disabled={imageIndex === images.length - 1}
                  >
                    ›
                  </button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
                    {images.map((_, idx) => (
                      <button
                        key={idx}
                        className={classNames(
                          "h-1.5 rounded-full transition-all",
                          idx === imageIndex ? "w-4 bg-white" : "w-1.5 bg-white/50"
                        )}
                        onClick={() => setImageIndex(idx)}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : item.thumbnail ? (
            <>
              <img src={item.thumbnail} aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40" />
              <img src={item.thumbnail} alt={item.name} className="relative z-10 w-full h-full max-h-[60vh] md:max-h-[90vh] object-contain" />
            </>
          ) : (
            <span className="text-white/50 text-sm">No media available</span>
          )}
        </div>

        {/* Details panel */}
        <div className="w-full md:w-96 p-6 flex flex-col gap-4 overflow-y-auto border-t md:border-t-0 md:border-l border-[#E5E7EB]">
          <h2 className="text-[18px] font-semibold text-[#111827] leading-snug break-words">
            {item.name || "—"}
          </h2>

          <div className="flex flex-col gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-[#6B7280] font-medium w-24 flex-shrink-0">Media Type</span>
              <span className="bg-[#F3F4F6] text-[#374151] px-2 py-0.5 rounded-md font-medium capitalize">
                {item.mediaType || (images.length > 0 ? "Image" : "Video")}
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#6B7280] font-medium w-24 flex-shrink-0">Guest Type</span>
              <span className="text-[#374151]">{getGuestTypeName(item.eligibilityCriteria)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#6B7280] font-medium w-24 flex-shrink-0">Status</span>
              <span className={classNames(
                "px-2 py-0.5 rounded-md text-xs font-semibold",
                item.isSelected ? "bg-green-100 text-green-700" : "bg-[#FEE2E2] text-[#B91C1C]"
              )}>
                {item.isSelected ? "Enabled" : "Disabled"}
              </span>
            </div>
            {!isVideo && images.length > 1 && (
              <div className="flex items-center gap-2">
                <span className="text-[#6B7280] font-medium w-24 flex-shrink-0">Images</span>
                <span className="text-[#374151]">{imageIndex + 1} / {images.length}</span>
              </div>
            )}
          </div>

          {/* Thumbnail strip */}
          {!isVideo && images.length > 1 && (
            <div className="mt-2">
              <span className="text-xs text-[#6B7280] font-medium mb-2 block">All Images</span>
              <div className="grid grid-cols-3 gap-1.5">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setImageIndex(idx)}
                    className={classNames(
                      "relative aspect-video rounded-md overflow-hidden border-2 transition-colors",
                      idx === imageIndex ? "border-secondary" : "border-transparent"
                    )}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OurStoryPreview;
